import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import DangerousIcon from "@mui/icons-material/Dangerous"
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined"
import RemoveCircleOutlineRoundedIcon from "@mui/icons-material/RemoveCircleOutlineRounded"
import SupportIcon from "@mui/icons-material/Support"
import WarningIcon from "@mui/icons-material/Warning"
import {
	Alert,
	AlertColor,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Grid,
	List,
	ListItem,
	ListItemText,
	Slide,
	Snackbar,
	Tooltip,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import {
	FormEvent,
	FunctionComponent,
	useCallback,
	useEffect,
	useReducer,
	useState,
} from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import { Link, useParams } from "react-router-dom"
import GuideDialog from "../../components/file/guide/dialog"
import FileUpload from "../../components/file/upload"
import BankAccountAutoComplete from "../../components/filters/bank-accounts"
import BankAutoComplete from "../../components/filters/bank-autocomplete"
import CampaignAutoComplete from "../../components/filters/campaign-autocomplete"
import CorporateAutoComplete from "../../components/filters/corporate-autocomplete"
import { CorporateEssential } from "../../store/actions/corporates"
import { uploadDocuments } from "../../store/actions/upload"
import { selectCorporate } from "../../store/corporates/corporates-all-slice"
import { RootState } from "../../store/reducers"
import { formatBytes } from "../../utils/format-bytes"

export interface IDistinteProperties {}

const Upload: FunctionComponent<IDistinteProperties> = () => {
	const { t } = useTranslation()
	const theme = useTheme()

	const { fileId } = useParams()
	const searchParams = new URLSearchParams(window.location.search)
	const fileName = searchParams.get("filename")

	const bankIdSelected: string | undefined = useSelector(
		(state: RootState) => state?.banksUser?.selected?.id
	)
	const corporateIdSelected: string | undefined = useSelector(
		(state: RootState) => state?.corporatesUser?.selected?.id
	)

	const corporateListState: CorporateEssential[] = useSelector(
		(state: RootState) => state.corporatesUser.list
	)

	const [filterBank, setFilterBank] = useState<undefined | string>(undefined)
	const [filterCorporate, setFilterCorporate] = useState<undefined | string>(
		undefined
	)
	const [filterCampaign, setFilterCampaign] = useState<undefined | string>(
		undefined
	)
	const [filterBankAccount, setFilterBankAccount] = useState<
		undefined | string
	>(undefined)
	const [isAllRequiredData, setIsAllRequiredData] = useState(false)
	const [isDialogConfirm, setIsDialogConfirm] = useState(false)
	const [isDialogSos, setIsDialogSos] = useState<boolean>(false)
	const [isConfirm, setIsConfirm] = useState(false)
	const [isShowedFooter, setIsShowedFooter] = useState(false)
	const [openSnackbarUploaded, setOpenSnackbarUploaded] =
		useState<boolean>(false)
	const [snackBarType, setSnackBarType] = useState<AlertColor | undefined>(
		undefined
	)

	const reducer = (state, action) => {
		switch (action.type) {
			case "IS_IN_DROP_ZONE": {
				return { ...state, inDropZone: action.inDropZone }
			}
			case "UPDATE_FILE_TO_LIST": {
				return { ...state, filesList: [action.files] }
			}
			case "ADD_FILE_TO_LIST": {
				return { ...state, filesList: state.filesList.concat(action.files) }
			}
			case "REMOVE_FILE_TO_LIST": {
				return {
					...state,
					filesList: state.filesList.filter((el) => {
						return el.name !== action.toRemove.name
					}),
				}
			}
			case "CLEAR_FILES": {
				return { inDropZone: false, filesList: [] }
			}
			case "SINGLE_DOCUMENT_UPLOAD_START": {
				const currentFile = state.filesList.find((el) => {
					return el.name === action?.payload.fileName
				})
				if (currentFile) currentFile.progress = action?.payload.progress
				return { ...state }
			}
			case "SINGLE_DOCUMENT_UPLOAD_SUCCESS":
			case "SINGLE_DOCUMENT_UPLOAD_ERROR": {
				const currentFile = state.filesList.find((el) => {
					return el.name === action?.payload.fileName
				})
				if (currentFile) currentFile.result = action?.payload.result
				return { ...state }
			}
			case "DOCUMENT_UPLOAD_SUCCESS": {
				setOpenSnackbarUploaded(true)
				setSnackBarType("success")
				return { ...state, allResults: "SUCCESSFUL" }
			}
			case "DOCUMENT_UPLOAD_WARNING": {
				setOpenSnackbarUploaded(true)
				// setSnackBarType("warning")
				setSnackBarType("error")
				return { ...state, allResults: "WARNING", errorsResult: action?.error }
			}
			case "DOCUMENT_UPLOAD_ERROR": {
				setOpenSnackbarUploaded(true)
				setSnackBarType("error")
				return {
					...state,
					allResults: "INTERNAL_SERVER_ERROR",
					errorsResult: action?.error,
				}
			}
			default: {
				return { ...state }
			}
		}
	}

	const [data, dispatch] = useReducer(reducer, {
		inDropZone: false,
		filesList: [],
		allResults: false,
		errorsResult: undefined,
	})

	const acceptedTypes: string[] = [
		"text/csv",
		"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
		"application/vnd.ms-excel",
	]

	useEffect(() => {
		if (bankIdSelected) setFilterBank(bankIdSelected)
	}, [bankIdSelected])

	useEffect(() => {
		if (corporateIdSelected) setFilterCorporate(corporateIdSelected)
	}, [corporateIdSelected])

	useEffect(() => {
		if (filterCorporate) {
			const infoCorporate = corporateListState?.find(
				(corporate: CorporateEssential) => corporate.id === filterCorporate
			)
			dispatch(selectCorporate(infoCorporate))
		}
	}, [filterCorporate])

	useEffect(() => {
		if (
			data.filesList &&
			data.filesList.filter((file) => acceptedTypes.includes(file.type))
				.length > 0
		) {
			if (!isShowedFooter) {
				setIsShowedFooter(true)
			}
		} else if (isShowedFooter) {
			setIsShowedFooter(false)
		}
	}, [data?.filesList])

	const renderFooter = () => {
		return isConfirm ? (
			<div className="footer">
				<div
					className="btn secondary"
					role="button"
					tabIndex={0}
					onClick={(e) => {
						dispatch({ type: "CLEAR_FILES" })
					}}
				>
					{t("dropZone.buttons.cancel", { ns: "upload" })}
				</div>
				<div
					className="btn primary"
					role="button"
					onClick={(e) => {
						// setIsShowedFooter(false) // remove confirm button footer
						setIsDialogConfirm(true)
					}}
				>
					{t("dropZone.buttons.confirm", {
						postProcess: "interval",
						count: data.filesList?.length ?? 0,
						ns: "upload",
					})}
				</div>
			</div>
		) : null
	}

	useEffect(() => {
		setIsAllRequiredData(
			!filterBank || !filterCampaign || !filterCorporate || !filterBankAccount
		)
	}, [fileId, filterBank, filterCampaign, filterCorporate, filterBankAccount])

	const handleFormSubmit = useCallback(() => {
		if (filterBank && filterCampaign && filterCorporate && filterBankAccount)
			uploadDocuments({
				fileId: fileId,
				bankId: filterBank,
				campaignId: filterCampaign,
				corporateSenderId: filterCorporate,
				bankAccount: filterBankAccount,
				files: data.filesList.filter((file: File) =>
					acceptedTypes.includes(file.type)
				),
				dispatchReact: dispatch,
			})
	}, [filterBank, fileId, filterCampaign, filterCorporate, filterBankAccount])

	const resetUploadForm = useCallback(() => {
		setFilterCampaign(undefined)
		setFilterBankAccount(undefined)
		if (bankIdSelected) setFilterBank(bankIdSelected)
		if (corporateIdSelected) setFilterCorporate(corporateIdSelected)
	}, [bankIdSelected, corporateIdSelected])

	return (
		<>
			<Grid
				container
				justifyContent={"flex-end"}
				sx={{ marginTop: "16px", marginBottom: "16px" }}
			>
				<Button
					onClick={() => setIsDialogSos(true)}
					sx={{ borderRadius: 0, textTransform: "initial" }}
					variant="contained"
					id="userSignInSubmit"
					type="button"
				>
					<SupportIcon
						sx={{ marginRight: "8px" }}
						aria-label={t("sos.accessibility.button", { ns: "upload" })}
					/>{" "}
					{t("sos.button.title", { ns: "upload" })}
				</Button>
			</Grid>
			<div>
				<Grid container justifyContent={"center"}>
					<h2 style={{ margin: "32px" }}>
						{fileId
							? t("dropZone.title.update", { ns: "upload", fileName: fileName })
							: t("dropZone.title.new", { ns: "upload" })}{" "}
					</h2>
				</Grid>
				{((data.filesList?.length === 0 && !isConfirm) || isShowedFooter) && (
					<Grid container justifyContent={"center"}>
						<FileUpload
							data={data}
							dispatch={dispatch}
							setIsConfirm={setIsConfirm}
						/>
					</Grid>
				)}
				<div className="dropped-files">
					{data.filesList
						? data.filesList?.map((f, index) => {
								const isAccepted = acceptedTypes.includes(f.type)
								let iconState = undefined

								switch (f.result?.outcome) {
									case "WARNING": {
										iconState = (
											<WarningIcon
												style={{
													color: "#fbc02d",
													transform: "translate(16px, -10px)",
												}}
											/>
										)
										break
									}
									case "SUCCESSFUL_UPLOAD": {
										iconState = (
											<CheckCircleOutlineIcon
												style={{
													color: "#388e3c",
													transform: "translate(16px, -10px)",
												}}
											/>
										)
										break
									}
									case "ERROR":
									case "EXCEPTION":
									case "FORBIDDEN":
									case "SENDER_ACCOUNT_EMPTY":
									case "FILE_SIZE_OVER":
									case "FILE_EMPTY":
									case "FILE_OVER_ROW":
									case "NOT_SUPPORTED_TYPE":
									case "NOT_SUPPORTED_TYPE_CSV":
									case "NOT_SUPPORTED_TYPE_XLS":
									case "NOT_SUPPORTED_VALID_TYPE":
									case "INTERNAL_SERVER_ERROR": {
										iconState = (
											<DangerousIcon
												style={{
													color: "#d32f2f",
													transform: "translate(16px, -24px)",
												}}
											/>
										)
										break
									}
									default: {
										iconState = undefined
										break
									}
								}

								if (f.progress > 0 && !f.result?.outcome) {
									iconState = (
										<CircularProgress
											style={{ transform: "translate(16px, -10px)" }}
											size="30px"
										/>
									)
								}

								return (
									<div key={f.fileName + index}>
										<Grid
											className="fileContainer"
											sx={{ borderColor: !isAccepted ? "#d32f2f" : "#c9d9e8" }}
											width={"300px"}
											alignItems={"center"}
											container
											justifyContent={"space-between"}
											key={f.name}
										>
											<Grid item>
												<InsertDriveFileOutlinedIcon />
											</Grid>

											<Grid
												item
												flex={1}
												sx={{
													margin: "0 16px",
													whiteSpace: "nowrap",
													overflow: "hidden",
													textOverflow: "ellipsis",
												}}
											>
												<div
													style={{
														minWidth: 0,
														whiteSpace: "nowrap",
														overflow: "hidden",
														textOverflow: "ellipsis",
														width: "100%",
													}}
												>
													<span
														style={{
															whiteSpace: "nowrap",
															overflow: "hidden",
															textOverflow: "ellipsis",
															fontSize: "13px",
															fontWeight: 600,
															color: !isAccepted ? "#d32f2f" : "inherit",
														}}
													>
														{f.name}
														<br></br>
														{f.result?.realMimeType ?? f.type}
													</span>
													<br />
													<span style={{ fontSize: "12px", fontWeight: 500 }}>
														{f.progress
															? formatBytes((f.size * f.progress) / 100, 2) +
																" di " +
																formatBytes(f.size, 2)
															: formatBytes(f.size, 2)}
													</span>
													<br />
													{!isAccepted && (
														<div style={{ fontSize: "12px", color: "#d32f2f" }}>
															Formato file non accettato. Carica file excel o
															csv.
														</div>
													)}
												</div>
											</Grid>
											<Grid
												item
												sx={{ paddingLeft: "16px", position: "relative" }}
											>
												{f.progress && f.progress === 100 ? (
													<div className="topIcon">
														<Tooltip
															describeChild
															title={t(`result.${f.result?.outcome}`, {
																ns: "upload",
																postProcess: "interval",
																count: f.result?.errors ?? 0,
																error: f.result?.errors,
																plicksCount: f.result?.plicksCount,
															})}
														>
															{iconState}
														</Tooltip>
														<br />
														<br />
														{f.result?.fileId && (
															<Tooltip
																describeChild
																title={t("button.gotoDetail", { ns: "upload" })}
															>
																<Link
																	to={`/distinta/${f.result?.fileId}?filename=${encodeURIComponent(f.name)}`}
																	state={{ filename: f.name }}
																>
																	<ArrowForwardIcon
																		style={{
																			color: theme.palette.primary.main,
																		}}
																	/>
																</Link>
															</Tooltip>
														)}
													</div>
												) : (
													<div
														role="button"
														tabIndex={0}
														onClick={(_e) => {
															setIsShowedFooter(false)
															setIsConfirm(false)
															dispatch({
																type: "REMOVE_FILE_TO_LIST",
																toRemove: f,
															})
														}}
													>
														<RemoveCircleOutlineRoundedIcon />
													</div>
												)}
											</Grid>
										</Grid>
										<div className="progressBar ">
											<div
												className="progressColored"
												style={{
													width: f.progress ? f.progress + "%" : 0,
													backgroundImage: !f.result
														? "linear-gradient(274deg, #00c3ea, #0090d1)"
														: f.result?.outcome === "WARNING"
															? "radial-gradient(circle at 10% 20%, rgb(255, 200, 124) 0%, rgb(252, 251, 121) 90%)"
															: f.result?.outcome === "SUCCESSFUL_UPLOAD"
																? "linear-gradient(75.7deg, rgb(34, 126, 34) 3.8%, rgb(99, 162, 17) 87.1%)"
																: "radial-gradient(circle at 81.9% 53.5%, rgb(173, 53, 53) 16.3%, rgb(240, 60, 60) 100.2%)",
													height: "100%",
												}}
											></div>
										</div>
									</div>
								)
							})
						: undefined}
				</div>
				{isShowedFooter ? renderFooter() : null}
			</div>
			{data.filesList && data.filesList.length > 0 && (
				<Dialog
					open={isDialogConfirm}
					onClose={() => setIsDialogConfirm(false)}
					PaperProps={{
						component: "form",
						sx: { width: "70vw" },
						onSubmit: (event: FormEvent<HTMLFormElement>) => {
							event.preventDefault()
							handleFormSubmit()
							setIsShowedFooter(false)
							setIsDialogConfirm(false)
						},
					}}
				>
					<DialogTitle>
						{t("dropZone.dialog.title", {
							postProcess: "interval",
							count: data.filesList?.length ?? 0,
							ns: "upload",
						})}
					</DialogTitle>
					<DialogContent>
						<span>
							{t("dropZone.dialog.subtitle", {
								postProcess: "interval",
								count: data.filesList?.length ?? 0,
								ns: "upload",
							})}{" "}
						</span>
						{data.filesList.map((f) => {
							return (
								<List
									key={"ListItem_" + f.name}
									sx={{
										width: "100%",
										maxWidth: 360,
										bgcolor: "background.paper",
										borderLeft: `3px solid ${theme.palette.primary.main}`,
									}}
								>
									<ListItem>
										<ListItemText
											primary={f.name}
											secondary={formatBytes(f.size, 2)}
										/>
									</ListItem>
								</List>
							)
						})}
						<br />
						<DialogContentText sx={{ paddingBottom: "16px" }}>
							{t("dropZone.dialog.bank", {
								postProcess: "interval",
								count: data.filesList?.length,
								ns: "upload",
							})}
						</DialogContentText>
						<BankAutoComplete
							disableClearable={false}
							filterBank={filterBank}
							setFilterBank={setFilterBank}
						/>
						<br />
						{!!filterBank && (
							<>
								<DialogContentText sx={{ paddingBottom: "16px" }}>
									{t("dropZone.dialog.corporate", {
										postProcess: "interval",
										count: data.filesList?.length,
										ns: "upload",
									})}
								</DialogContentText>
								<CorporateAutoComplete
									filterCorporate={filterCorporate}
									setFilterCorporate={setFilterCorporate}
								/>
							</>
						)}
						{filterCorporate && (
							<>
								<br />
								<DialogContentText sx={{ paddingBottom: "16px" }}>
									{t("dropZone.dialog.campaign", {
										postProcess: "interval",
										count: data.filesList?.length,
										ns: "upload",
									})}
								</DialogContentText>
								<CampaignAutoComplete
									filterCorporate={filterCorporate}
									filterCampaign={filterCampaign}
									setFilterCampaign={setFilterCampaign}
								/>
							</>
						)}
						{filterCampaign && (
							<>
								<br />
								<DialogContentText sx={{ paddingBottom: "16px" }}>
									{t("dropZone.dialog.cc", {
										postProcess: "interval",
										count: data.filesList?.length,
										ns: "upload",
									})}
								</DialogContentText>
								<BankAccountAutoComplete
									filterBankAccount={filterBankAccount}
									setFilterBankAccount={setFilterBankAccount}
								/>
							</>
						)}
					</DialogContent>
					<DialogActions>
						<Grid container justifyContent={"space-between"}>
							<Button onClick={() => resetUploadForm()}>
								{t("dropZone.buttons.cancel", { ns: "upload" })}
							</Button>
							<Button type="submit" disabled={isAllRequiredData}>
								{t("dropZone.buttons.send", { ns: "upload" })}
							</Button>
						</Grid>
					</DialogActions>
				</Dialog>
			)}
			<GuideDialog open={isDialogSos} onClose={() => setIsDialogSos(false)} />

			<Dialog
				open={false}
				onClose={() => setIsDialogConfirm(false)}
				PaperProps={{
					component: "form",
					onSubmit: (event: FormEvent<HTMLFormElement>) => {
						event.preventDefault()
						/*const formData = new FormData(event.currentTarget);
                        const formJson = Object.fromEntries((formData as any).entries());*/
						setIsDialogConfirm(false)
					},
				}}
			>
				<DialogTitle>{t("sos.dialog.title", { ns: "upload" })}</DialogTitle>
				<DialogContent>
					<span>
						{t("dropZone.dialog.subtitle", {
							postProcess: "interval",
							count: data.filesList?.length,
							ns: "upload",
						})}{" "}
					</span>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => {}}>Cancel</Button>
					<Button type="submit">Subscribe</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				TransitionComponent={Slide}
				open={openSnackbarUploaded}
				autoHideDuration={25000}
				onClose={() => setOpenSnackbarUploaded(false)}
			>
				<Alert
					onClose={() => setOpenSnackbarUploaded(false)}
					severity={snackBarType}
					variant="filled"
					sx={{ width: "calc(100vw - 48px)" }}
				>
					{t(`snackbar.result.${data?.allResults}`, {
						ns: "upload",
						postProcess: "interval",
						count: data.filesList?.length ?? 0,
						fileLength: data.filesList?.length,
						errors: data.errorsResult?.errorFiles ?? 0,
						warnings: data.errorsResult?.warningFiles ?? 0,
					})}
				</Alert>
			</Snackbar>
		</>
	)
}

export default Upload
